import { useEffect, useState, useRef } from 'react';
// mantine
import {
    Combobox,
    Loader,
    CloseButton,
    InputBase,
    useCombobox
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
// utils
import type { TPersona, TPersonasParams } from '@newo/api/gateway';
import { usePersonasQuery } from '@/services/bff/bff.queries.ts';

const DEBOUNCE_TIMEOUT = 500;
const SLICE_LENGTH = 4;

interface PersonasAutocompleteProps {
    className?: string;
    label?: string;
    placeholder?: string;
    personaName?: string;
    onSelectAsync?: (persona?: TPersona) => Promise<void>;
    onSelect?: (persona?: TPersona) => void;
    debounceTimeout?: number;
    params?: Partial<TPersonasParams>;
}

const PersonasAutocomplete = ({
    className,
    label,
    placeholder,
    onSelect,
    onSelectAsync,
    personaName,
    debounceTimeout = DEBOUNCE_TIMEOUT,
    params
}: PersonasAutocompleteProps) => {
    const [searchTerm, setSearchTerm] = useState(personaName ?? '');
    const [debouncedSearchTerm] = useDebouncedValue(
        searchTerm,
        debounceTimeout,
        {
            leading: true
        }
    );
    const {
        data: personas,
        isLoading,
        isSuccess
    } = usePersonasQuery({
        page: 1,
        per: 50,
        query: debouncedSearchTerm,
        ...params
    });

    useEffect(() => {
        setSearchTerm(personaName ?? '');
    }, [personaName]);

    const options = personas?.items ?? [];
    const combobox = useCombobox({
        onDropdownClose: () => {
            combobox.resetSelectedOption();
        }
    });
    const inputRef = useRef<HTMLInputElement>(null);

    const handleSelect = (personaId: string) => {
        const p = personas?.items.find((item) => item.id === personaId);
        setSearchTerm(p?.name ?? '');
        onSelect?.(p);
        onSelectAsync?.(p);
        combobox.closeDropdown();
    };

    const handleClear = () => {
        setSearchTerm('');
        onSelect?.();
        onSelectAsync?.();
        combobox.resetSelectedOption();
        inputRef.current?.focus();
    };

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(event.target.value);
        combobox.openDropdown();
    };

    return (
        <div className={className}>
            <Combobox
                store={combobox}
                withinPortal={false}
                onOptionSubmit={handleSelect}
            >
                <Combobox.Target>
                    <InputBase
                        autoComplete="off"
                        label={label}
                        placeholder={placeholder}
                        ref={inputRef}
                        rightSection={
                            isLoading ? (
                                <Loader size="1rem" />
                            ) : (
                                <CloseButton
                                    size="xs"
                                    style={{
                                        display: searchTerm ? undefined : 'none'
                                    }}
                                    onClick={handleClear}
                                />
                            )
                        }
                        rightSectionPointerEvents={searchTerm ? 'auto' : 'none'}
                        size="xs"
                        value={searchTerm}
                        onChange={handleInputChange}
                        onClick={() => {
                            combobox.openDropdown();
                        }}
                    />
                </Combobox.Target>
                <Combobox.Dropdown>
                    <Combobox.Options className="overflow-auto" mah={250}>
                        {isLoading ? (
                            <Combobox.Empty>
                                <Loader size="1rem" />
                            </Combobox.Empty>
                        ) : undefined}
                        {isSuccess && options.length === 0 ? (
                            <Combobox.Empty>No Records</Combobox.Empty>
                        ) : undefined}
                        {isSuccess && options.length > 0
                            ? options.map((persona) => (
                                  <Combobox.Option
                                      key={persona.id}
                                      value={persona.id}
                                  >
                                      <div>{persona.name}</div>
                                      <div className="text-xs text-gray-6">
                                          Id:{' '}
                                          {`${persona.id.slice(0, SLICE_LENGTH)}...${persona.id.slice(-SLICE_LENGTH)}`}
                                      </div>
                                  </Combobox.Option>
                              ))
                            : undefined}
                    </Combobox.Options>
                </Combobox.Dropdown>
            </Combobox>
        </div>
    );
};

export default PersonasAutocomplete;
